import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user';
import * as Sentry from "@sentry/vue";

import MainLayout from './layouts/Main.vue';
import MainContainedLayout from './layouts/MainContained.vue';
import { devRoutes } from './router-dev.js';
import { adminRoutes } from './router-admin.js'

import { usePermissionsStore } from '@/stores/permissions'
import { PERMISSIONS } from '@/composables/policies/permissions'
import { useAppInit } from '@/composables/useAppInit.js'
import { useClubDashboardPolicy } from '@/composables/policies/useClubDashboardPolicy'
import { useEventFormPolicy } from '@/composables/policies/useEventFormPolicy'
import { useReimbursementRequestRoutePolicy } from '@/composables/policies/useReimbursementRequestRoutePolicy'
import { useClubProfileRequestRoutePolicy } from '@/composables/policies/useClubProfileRequestRoutePolicy'
import { useClubApplicationRoutePolicy } from '@/composables/policies/useClubApplicationRoutePolicy'
import { useTermAcknowledgementFormRoutePolicy } from '@/composables/policies/useTermAcknowledgementFormRoutePolicy'
import TermAcknowledgementFormService from '@/services/clubs/termAcknowledgementFormService'
import { handleRequest } from '@/utils/handleRequest';

const DEFAULT_TITLE = 'Home';

// Guard for executive dashboard tabs: fetches fresh club permissions and
// checks the named policy method.
const requireDashboardPermission = (check) => async (to, _from, next) => {
  const policy = useClubDashboardPolicy(to.params.id)
  await policy.fetchPermissions()
  policy[check]() ? next() : next({ name: 'permission-denied' })
}

const routes = [
  {
    path: '/auth-response',
    name: 'auth-response',
    component: () => import('./components/auth/AuthResponse.vue'),
    meta: { title: 'Signing In…', layout: MainLayout }
  },
  {
    path: '/',
    component: () => import("./views/Home.vue"),
    meta: { layout: MainLayout }
  },
  {
    path: "/login",
    name: "login",
    component: () => import("./views/Login.vue"),
    meta: { title: 'Login', layout: null }
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("./components/auth/LogoutButton.vue"),
    meta: { layout: MainLayout }
  },
  {
    path: '/find-a-club',
    name: "find-a-club",
    component: () => import('./views/clubs-listing/ClubsListing.vue'),
    meta: { title: 'Find a Club', layout: MainContainedLayout }
  },
  // {
  //   path: '/important-dates',
  //   name: "important-dates",
  //   // component: ImportantDatesPage,
  //   meta: { title: 'Important Dates', layout: MainContainedLayout }
  // },
  {
    path: '/faq',
    name: "faq",
    component: () => import('./views/home/Faq.vue'),
    meta: { title: 'Frequently Asked Questions', layout: MainContainedLayout }
  },
  {
    path: '/resources',
    name: "resources",
    component: () => import('./views/home/Resources.vue'),
    meta: { title: 'Resources', layout: MainContainedLayout }
  },
  {
    path: '/av-catalogue',
    name: "av-catalogue",
    component: () => import('./views/home/AvCatalogue/AvCatalogue.vue'),
    meta: { title: 'AV Catalogue', layout: MainContainedLayout }
  },
  {
    path: '/resource/event-planning',
    name: "event-planning",
    component: () => import('./views/home/EventPlanning.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/resource/financial-planning',
    name: "financial-planning",
    component: () => import('./views/home/FinancialPlanning.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/resource/event-meeting-spaces',
    name: "event-meeting-spaces",
    component: () => import('./views/home/EventMeetingSpaces.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/resource/clubs-societies-day',
    name: "clubs-societies-day",
    component: () => import('./views/home/ClubsSocietiesDay.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/resource/it-support',
    name: "it-support",
    component: () => import('./views/home/ItSupport.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/resource/marketing-support',
    name: "marketing-support",
    component: () => import('./views/home/MarketingSupport.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/resource/free-storage-space',
    name: "free-storage-space",
    component: () => import('./views/home/FreeStorageSpace.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/shared/forms/:token',
    name: 'shared-form-preview',
    component: () => import('./views/public/SharedFormPreview.vue'),
    meta: { layout: MainContainedLayout }
  },
  {
    path: '/c/:slug',
    name: 'club-bio-card',
    component: () => import('./views/public/ClubBioCard.vue'),
    meta: { title: 'Club', layout: null }
  },
  {
    path: '/club/:id',
    children: [
      {
        path: "",
        name: "club",
        component: () => import('./views/club/ClubShow.vue'),
        meta: { title: 'Club', layout: MainLayout },
      },
      {
        path: 'constitution',
        name: "club-constitution",
        component: () => import('./views/club/ClubConstitution.vue'),
        meta: { title: 'Club Constitution', layout: MainLayout }
      },
      {
        path: 'no-constitution',
        name: 'club-no-constitution',
        component: () => import('./views/club/MissingConstitution.vue'),
        meta: { title: 'Missing Constitution', layout: MainLayout }
      },
      {
        path: 'executive-dashboard',
        name: "executive-dashboard",
        component: () => import('./views/club/ClubDashboard.vue'),
        meta: { title: 'Club Dashboard', layout: MainLayout, requiresAuth: true },
        redirect: { name: 'executive-dashboard-overview' },
        beforeEnter: async (to, _from, next) => {
          const policy = useClubDashboardPolicy(to.params.id)
          const result = await policy.fetchPermissions()
          result?.success ? next() : next({ name: 'permission-denied' })
        },
        children: [
          {
            path: 'overview',
            name: 'executive-dashboard-overview',
            components: { dashboard: () => import('./views/club/components/dashboard/Overview.vue')},
            meta: { title: 'Club Dashboard - Overview', layout: MainLayout }
          },
          {
            path: 'events',
            name: 'executive-dashboard-events',
            components: { dashboard: () => import('./views/club/components/dashboard/Events.vue')},
            meta: { title: 'Club Dashboard - Events', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewEventForms')
          },
          {
            path: 'reimbursement-requests',
            name: 'executive-dashboard-cheque-requests',
            components: { dashboard: () => import('./views/club/components/dashboard/ChequeRequests.vue')},
            meta: { title: 'Club Dashboard - Reimbursement Requests', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewReimbursementRequests')
          },
          {
            path: 'transactions',
            name: 'executive-dashboard-transactions',
            components: { dashboard: () => import('./views/club/components/Transactions.vue')},
            meta: { title: 'Club Dashboard - Transactions', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewFinances')
          },
          {
            path: 'members',
            name: 'executive-dashboard-members',
            components: { dashboard: () => import('./views/club/components/dashboard/Members.vue')},
            meta: { title: 'Club Dashboard - Members', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewMembers')
          },
          {
            path: 'past-members',
            components: { dashboard: () => import('./views/club/components/dashboard/PastMembers.vue')},
            meta: { title: 'Club Dashboard - Past Members', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewMembers'),
            children: [
              { 
                name: 'executive-dashboard-members-past-members',
                path: '', 
                component: () => import('./views/club/components/dashboard/PastMembers/TermSelection.vue') 
              },
              {
                path: ':termId',
                name: 'executive-dashboard-members-past-members-term',
                component: () => import('./views/club/components/dashboard/PastMembers/PastMembers.vue')
              }
            ]
          },
          {
            path: 'support',
            name: 'executive-dashboard-support',
            components: { dashboard: () => import('./views/club/components/dashboard/Support.vue')},
            meta: { title: 'Club Dashboard - Support', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewSupport')
          },
          {
            path: 'profile-requests',
            name: 'club-profile-requests',
            components: { dashboard: () => import('./views/club/club-profile-requests/Index.vue')},
            meta: { title: 'Club Dashboard - Profile Requests', layout: MainLayout },
            beforeEnter: requireDashboardPermission('canViewClubProfileRequests')
          }
        ]
      },
      {
        path: 'executive-dashboard/requests/club-email',
        name: "club-email-request",
        component: () => import('./views/club/ClubEmailRequest.vue'),
        meta: { title: 'Club Email Request', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useClubDashboardPolicy(to.params.id)
          await policy.fetchPermissions()
          policy.canSubmitClubEmailRequest() ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'update-club-profile',
        component: () => import('./views/club/club-profile-update/Edit.vue'),
        meta: { title: 'Update Club Profile', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useClubDashboardPolicy(to.params.id)
          await policy.fetchPermissions()
          policy.canUpdateClubProfile() ? next() : next({ name: 'permission-denied' })
        },
        children: [
          {
            path: '',
            name: 'update-club-profile',
            component: () => import('./views/club/club-profile-update/Information.vue'),
          },
          {
            path: 'description',
            name: 'update-club-profile-description',
            component: () => import('./views/club/club-profile-update/Description.vue'),
          },
          {
            path: 'socials',
            name: 'update-club-profile-socials',
            component: () => import('./views/club/club-profile-update/Socials.vue'),
          },
          {
            path: 'links',
            name: 'update-club-profile-links',
            component: () => import('./views/club/club-profile-update/Links.vue'),
          },
          {
            path: 'gallery',
            name: 'update-club-profile-gallery',
            component: () => import('./views/club/club-profile-update/Gallery.vue'),
          },
        ],
      },
      {
        path: 'club-term-acknowledgement',
        name: "club-term-acknowledgement",
        component: () => import('./views/club/ClubTermAcknowledgement.vue'),
        meta: { title: 'Club Term Acknowledgement', layout: MainLayout, requiresAuth: true },
        // Deliberately not requireDashboardPermission: that endpoint is gated to
        // current-term executives only, but a club that skipped a term has no
        // current-term membership at all — this flow is for that club's last
        // active term's signing authorities to reactivate it, so it checks a
        // dedicated permission instead (see ClubPolicy#view_term_acknowledgement_form_submission_alert?).
        beforeEnter: async (to, _from, next) => {
          const service = TermAcknowledgementFormService.create()
          const result = await handleRequest(() => service.getTermAcknowledgementFormPermissions(to.params.id))
          result?.success !== false ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'club-term-acknowledgement/:term_acknowledgement_form_id',
        name: "club-term-acknowledgement-show",
        component: () => import('./views/club/ClubTermAcknowledgementShow.vue'),
        meta: { title: 'Club Term Acknowledgement', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useTermAcknowledgementFormRoutePolicy(to.params.term_acknowledgement_form_id)
          await policy.fetchTermAcknowledgementForm()
          policy.canShow() ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: "club-term-acknowledgement/draft/:draftId",
        name: "club-term-acknowledgement-draft",
        component: () => import('./views/club/ClubTermAcknowledgement.vue'),
        meta: { title: 'Club Term Acknowledgement', layout: MainLayout, requiresAuth: true },
        props: route => ({ draftId: route.params.draftId }),
        beforeEnter: async (to, _from, next) => {
          const policy = useTermAcknowledgementFormRoutePolicy(to.params.draftId)
          const ok = await policy.canRestoreDraft()
          ok ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'club-term-acknowledgement/:term_acknowledgement_form_id/success',
        name: "club-term-acknowledgement-submission-success",
        component: () => import('./views/club/ClubTermAcknowledgementFormSuccess.vue'),
        meta: { title: 'Club Term Acknowledgement', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useTermAcknowledgementFormRoutePolicy(to.params.term_acknowledgement_form_id)
          await policy.fetchTermAcknowledgementForm()
          policy.canShow() ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'new-reimbursement-request',
        name: "new-reimbursement-request",
        component: () => import('./views/club/reimbursement-requests/New.vue'),
        meta: { title: 'Club New Reimbursement Request', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useReimbursementRequestRoutePolicy(to.params.id)
          const ok = await policy.canCreate(to.params.id)
          ok ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: "new-reimbursement-request/draft/:draftId",
        name: "new-reimbursement-request-draft",
        component: () => import('./views/club/reimbursement-requests/New.vue'),
        meta: { title: 'Club New Reimbursement Request', layout: MainLayout, requiresAuth: true },
        props: route => ({ draftId: route.params.draftId }),
        beforeEnter: async (to, _from, next) => {
          const policy = useReimbursementRequestRoutePolicy(to.params.draftId)
          const ok = await policy.canRestoreDraft()
          ok ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'reimbursement-request/:rmbId',
        name: "show-reimbursement-request",
        component: () => import('./views/club/reimbursement-requests/Show.vue'),
        meta: { title: 'Club Reimbursement Request', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useReimbursementRequestRoutePolicy(to.params.rmbId)
          await policy.fetchReimbursementRequest()
          policy.canShow() ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'reimbursement-request/:rmbId/edit',
        name: "edit-reimbursement-request",
        component: () => import('./views/club/reimbursement-requests/Edit.vue'),
        meta: { title: 'Club Reimbursement Request', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useReimbursementRequestRoutePolicy(to.params.rmbId)
          await policy.fetchReimbursementRequest()
          policy.canUpdate() ? next() : next({ name: 'show-reimbursement-request', params: to.params })
        }
      },
      {
        path: 'club-profile-request/:cprId',
        name: "club-profile-request-show",
        component: () => import('./views/club/club-profile-requests/Show.vue'),
        meta: { title: 'Club Profile Request', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useClubProfileRequestRoutePolicy(to.params.id, to.params.cprId)
          await policy.fetchClubProfileRequest()
          policy.canShow() ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'event-form',
        name: "club-event-new",
        component: () => import('./views/club/events/form/New.vue'),
        meta: { title: 'Event Request Form', layout: MainLayout, requiresAuth: true },
        beforeEnter: requireDashboardPermission('canViewEventForms')
      },
      {
        path: 'event-form/:eventFormId',
        name: "club-event-show",
        component: () => import('./views/club/events/form/Show.vue'),
        meta: { title: 'Event Request', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useEventFormPolicy(to.params.eventFormId)
          await policy.fetchEventForm()
          policy.canView() ? next() : next({ name: 'permission-denied' })
        }
      },
      {
        path: 'event-form/:eventFormId/edit',
        name: "club-event-edit",
        component: () => import('./views/club/events/form/Edit.vue'),
        meta: { title: 'Event Request Form', layout: MainLayout, requiresAuth: true },
        beforeEnter: async (to, _from, next) => {
          const policy = useEventFormPolicy(to.params.eventFormId)
          await policy.fetchEventForm()
          policy.canEdit() ? next() : next({ name: 'club-event-show', params: to.params })
        }
      }
    ]
  },
  {
    path: '/start-a-club',
    name: "club-new",
    component: () => import('@/views/club-application/New.vue'),
    meta: { title: 'Start a Club', layout: MainLayout, requiresAuth: true }
  },
  {
    path: "/start-a-club/draft/:draftId",
    name: "club-draft",
    component: () => import('@/views/club-application/New.vue'),
    meta: { title: 'Start a Club', layout: MainLayout, requiresAuth: true },
    props: route => ({ draftId: route.params.draftId }),
    beforeEnter: async (to, _from, next) => {
      const policy = useClubApplicationRoutePolicy(to.params.draftId)
      const ok = await policy.canRestoreDraft()
      ok ? next() : next({ name: 'permission-denied' })
    }
  },
  {
    path: "/club-applications/:id",
    name: "club-applications-show",
    component: () => import("@/views/club-application/Show.vue"),
    meta: { title: 'Club Applications', layout: MainLayout, requiresAuth: true },
    beforeEnter: async (to, _from, next) => {
      const policy = useClubApplicationRoutePolicy(to.params.id)
      await policy.fetchClubApplication()
      policy.canShow() ? next() : next({ name: 'permission-denied' })
    }
  },
  {
    path: "/club-applications/:id/success",
    name: "club-application-submission-success",
    component: () => import("@/views/club-application/SubmissionSuccess.vue"),
    meta: { title: 'Club Applications', layout: MainLayout, requiresAuth: true },
    beforeEnter: async (to, _from, next) => {
      const policy = useClubApplicationRoutePolicy(to.params.id)
      await policy.fetchClubApplication()
      policy.canShow() ? next() : next({ name: 'permission-denied' })
    }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('./views/user/NotificationsList.vue'),
    meta: { title: 'Notifications', layout: MainContainedLayout, requiresAuth: true }
  },
  {
    path: '/manage-club',
    name: "manage-club",
    component: () => import("./views/user/ManageClub.vue"),
    meta: { title: 'Manage Club', layout: MainContainedLayout, requiresAuth: true },
    redirect: { name: 'manage-club-memberships' },
    children: [
      {
        path: 'memberships',
        name: 'manage-club-memberships',
        components: { manageDetail: () => import("./views/user/components/manage-clubs/ClubMembershipList.vue") },
        meta: { title: 'Manage Club - Memberships', layout: MainContainedLayout }
      },
      {
        path: 'following',
        name: 'manage-club-following',
        components: { manageDetail: () => import("./views/user/components/manage-clubs/FollowedClubsList.vue") },
        meta: { title: 'Manage Club - Following', layout: MainContainedLayout, requiresAuth: true }
      },
      {
        path: 'applications',
        name: 'manage-club-applications',
        components: { manageDetail: () => import("./views/user/components/manage-clubs/ApplicationList.vue") },
        meta: { title: 'Manage Club - Applications', layout: MainContainedLayout }
      },
      {
        path: 'past-memberships',
        name: 'manage-club-past-memberships',
        components: { manageDetail: () => import("./views/user/components/manage-clubs/PastMembershipsList.vue") },
        meta: { title: 'Manage Club - Past Memberships', layout: MainContainedLayout }
      },
      {
        path: 'action-items',
        name: 'manage-club-action-items',
        components: { manageDetail: () => import("./views/user/components/manage-clubs/ActionItems.vue") },
        meta: { title: 'Manage Club - Action Items', layout: MainContainedLayout }
      }
    ]
  },
  {
    path: '/my-profile',
    component: () => import("./views/user/Profile.vue"),
    meta: { title: 'Profile', layout: MainLayout, requiresAuth: true },
    children: [
      {
        path: '',
        name: "my-profile",
        component: () => import("./views/user/components/profile/ProfileDetails.vue"),
      },
      {
        path: 'billing',
        name: "my-profile-billing",
        component: () => import("./views/user/components/profile/Billing.vue"),
      },
      {
        path: 'address',
        name: "my-profile-address",
        component: () => import("./views/user/components/profile/Address.vue"),
      },
      {
        path: 'reimbursements',
        name: "my-profile-reimbursements",
        component: () => import("./views/user/components/profile/Reimbursements.vue"),
      },
      {
        path: 'notifications',
        name: "my-profile-notifications",
        component: () => import("./views/user/NotificationsList.vue"),
      },
      {
        path: 'points',
        name: "my-profile-points",
        component: () => import("./views/user/components/profile/Points.vue"),
      },
      {
        path: 'settings',
        name: "my-profile-settings",
        component: () => import("./views/user/components/profile/Settings.vue"),
      },
    ]
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('./views/Error.vue'),
    meta: { title: 'Error', layout: MainLayout }
  },
  {
    path: '/permission-denied',
    name: 'permission-denied',
    component: () => import('./views/PermissionDenied.vue'),
    meta: { title: 'Permission Denied', layout: MainLayout }
  },
  {
    path: '/page-not-found',
    name: 'page-not-found',
    component: () => import('./views/PageNotFound.vue'),
    meta: { title: 'Page Not Found', layout: MainLayout }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    beforeEnter: (to, from, next) => {
      Sentry.captureMessage(`User attempted to access undefined route: ${to.fullPath}`, {
        level: 'warning',
        tags: {
          route_error: 'undefined_route'
        },
        extra: {
          attempted_path: to.fullPath,
          from_path: from.fullPath,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });
      next({ name: 'page-not-found' });
    },
    meta: { layout: MainLayout }
  }
];

routes.push(adminRoutes);

// tree shaking dev routes
if (__DEV__) {
  routes.push(devRoutes);
}

const router = createRouter({
  history: createWebHistory('/'),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }
    return { top: 0 };
  },
  routes: routes
});

// Layouts fallback if not defined in route
router.beforeEach(async (to, from, next) => {
  if (to.meta.layout === undefined) {
    to.meta.layout = MainLayout;
  }
  const { initialize } = useAppInit()
  await initialize()

  const perms = usePermissionsStore()

  if (to.meta.requiresAuth) {
    if (!validateAuth(to.fullPath, next)) return;
  }

  for (const route of to.matched) {
    const required = route.meta.permissions;
    if (!required || required.length === 0) continue;

    const mode = (route.meta.permissionMode) || 'all'
    const hasAccess = mode === 'any'
      ? perms.canAny(required)
      : perms.canAll(required);

    if (!hasAccess) {
      return next({ name: 'permission-denied' });
    }
  }

  next();
  
});

router.afterEach((to, from) => {
  let title = to.meta.title || DEFAULT_TITLE;

  document.title = `WUSA Clubs - ${title}`;
});

// Logined users only
function validateAuth(to_url, next) {
  const userStore = useUserStore();
  if (userStore.isLoggedIn) {
    return true;
  } else {
    userStore.setLoginRedirectUrl(to_url);
    next('/login');
    return false;
  }
}

export default router
